import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { sanitizeHtml } from '../utils/richText';
import { articleImage, articleHasArt, categoryTheme } from './articleArt';

// ROO-202: in-app reader for a learning-resources article. Content is authored
// HTML served by GET /api/students/:id/resources/:slug and rendered sanitized —
// no external links, so nothing can break.

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface Article {
  title: string;
  subject: string;
  category: string | null;
  content: string;
  readMinutes: number | null;
}

interface Props {
  studentId: string;
  slug: string;
  onBack: () => void;
}

const ArticleView: React.FC<Props> = ({ studentId, slug, onBack }) => {
  const [article, setArticle] = useState<Article | null>(null);
  const [state, setState] = useState<'loading' | 'error' | 'ok'>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading');
    const token =
      localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
    axios
      .get(`${API_BASE_URL}/api/students/${studentId}/resources/${encodeURIComponent(slug)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (!alive) return;
        if (res.data?.success) {
          setArticle(res.data.data);
          setState('ok');
        } else {
          setState('error');
        }
      })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [studentId, slug]);

  return (
    <section style={{ maxWidth: 760, margin: '0 auto', paddingTop: 24 }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          border: '1.5px solid #E5DCC3', background: '#fff', color: '#1A2A1F',
          borderRadius: 999, padding: '6px 16px', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16,
        }}
      >
        ← All resources
      </button>

      {state === 'loading' && (
        <div className="cvr-card"><p style={{ color: 'var(--mute)', margin: 0 }}>Loading…</p></div>
      )}
      {state === 'error' && (
        <div className="cvr-card">
          <p style={{ color: 'var(--coral)', margin: 0 }}>Couldn’t load this article. Please try again.</p>
        </div>
      )}
      {state === 'ok' && article && (
        <article className="cvr-card cvr-article" style={{ overflow: 'hidden', padding: 0 }}>
          {articleHasArt(slug) && (
            <img
              src={articleImage(slug)!}
              alt=""
              style={{ height: 220, width: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          <div style={{ padding: '20px 24px 24px' }}>
          {article.category && (
            <div style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase', color: categoryTheme(article.category).accent, marginBottom: 6,
            }}>
              {article.category}
              {article.readMinutes ? ` · ${article.readMinutes} min read` : ''}
            </div>
          )}
          <h1 style={{ fontSize: 26, lineHeight: 1.25, color: 'var(--ink)', margin: '0 0 16px' }}>
            {article.title}
          </h1>
          <div
            className="cvr-article__body"
            style={{ fontSize: 16, lineHeight: 1.7, color: '#374151' }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content) }}
          />
          </div>
        </article>
      )}
    </section>
  );
};

export default ArticleView;
